import { Grid, GridSize, Stack, TextField, Typography } from "@mui/material"
import Section from "../../../components/Section"
import HeadingBar, { HeadingBarItem } from "../../../components/HeadingBar"
import SimpleTable from "../../../components/SimpleTable"
import { ReactNode, useContext, useEffect, useState } from "react"
import { CompanionWeapon, CompanionWeaponBlockBase, InteractableSave, PlayerCharacter } from "../../../model/character"
import { CharacterContext } from "../../../model/characterContext";

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import { loadLibraryData, makeListData } from "../../../master/libraryData"
import { HexList } from "../../../components/HexList"
import { Modal } from "../../../components/Modal"
import { CompanionWeaponEditor, GetNewCompanion } from "./CompanionWeaponEditor"
import { v4 } from 'uuid';
import { defaultCompanionWeaponBase } from "../../../master/defaults"

const GridRow = (props: { gridSize: GridSize, justifyContent?: "center" | "flex-start" | "flex-end", children?: ReactNode }) => {
        return <Grid item xs={props.gridSize} ><Stack direction="row" justifyContent={props.justifyContent} alignItems="center" height="100%">{props.children}</Stack></Grid>
    }

interface CompanionWeaponListProps {
    setCharacter: (value: PlayerCharacter) => void
}

const defaultWeapon: CompanionWeapon = {
    base: {
        ...defaultCompanionWeaponBase
    },
    variations: [],
    modifications: [],
    id: "",
    isStashed: false,
    name: "New Companion (Weapon)",
    description: "",
    munitionsSpent: 0,
    reloadsPerformed: 0,
}

const NewModal = (props: {show: boolean, onClose: () => void, addNewCompanion: (value: CompanionWeaponBlockBase) => void}) => {
        const [ searchTerm, setSearchTerm ] = useState("");

        const [ itemList, setItemList ] = useState<InteractableSave[]>([]);

        const LoadItem = (id: string) => {
            let block: CompanionWeaponBlockBase = { ...defaultCompanionWeaponBase};
            block.id = v4();
            if(id !== "_custom"){
                const data = loadLibraryData<CompanionWeaponBlockBase>("lcomwep", id ?? "NONE");
                if(data !== undefined){
    
                    block = {
                        ...data,
                        id: block.id
                    }
                }
            }
            props.onClose();
            props.addNewCompanion(block);
        }

        const MakeItemList = (): InteractableSave[] => {
            return makeListData("lcomwep", (id) => LoadItem(id))
        }

        useEffect(() => {
            setItemList(MakeItemList());
            
            setSearchTerm("");
        });

        useEffect(() => { setSearchTerm("")}, [props.show]);

        const actions = [
                {
                    label: "Cancel",    
                    onClick: props.onClose,
                    size:12
                }
            ];

        return <Modal title="Add Companion Weapon" actions={actions} onClose={props.onClose} open={props.show}>
            <TextField fullWidth
                placeholder="Search"
                type="text" 
                variant="standard"
                value={searchTerm}
                style={{marginBottom: 1}}
                onChange={(event) => setSearchTerm(event.currentTarget.value)} />
            <HexList
                items={itemList.filter((item) => searchTerm === "" || item.name.toLowerCase().includes(searchTerm.toLowerCase()))}
             />
        </Modal>
    }

const CompanionWeaponEditorList = (props: CompanionWeaponListProps) => {
    const characterContext = useContext(CharacterContext)!;
    const [ showAddModal, setShowAddModal ] = useState(false);
    const [ comWeapon, setComWeapon ] = useState<CompanionWeapon | undefined>();
    
    const { setCharacter } = props;
    const { character, characterDisplay } = characterContext;

    const handleModalCancel = () => {
        setComWeapon(undefined);
    }

    const handleAddCompanion = (companion: CompanionWeaponBlockBase) => {

        const weapon = { ... defaultWeapon };
        weapon.base = companion;
        weapon.name = companion.name ?? "";
        weapon.id = v4();

        const list = [...character.companionWeapons, weapon];
        setCharacter({
            ...character,
            companionWeapons: list
        })
        setComWeapon(weapon);
    }

    const handleModalSubmit = (companion: CompanionWeapon) => {
        if(!companion) return;
        const index = character.companionWeapons.findIndex(tg => tg.id === companion.id);
        const list = [...character.companionWeapons];
        
        if(index === -1) {
            list.push(companion);
        } else {
            list[index] = companion;
        }
        setCharacter({
            ...character,
            companionWeapons: list
        })
        setComWeapon(undefined);
    }

    const handleRemove= (key: string) => {
        const index = character.companionWeapons.findIndex(tg => tg.id === key);
            if(index !== undefined){
                const list = [...character.companionWeapons]
                list.splice(index,1);
                setCharacter({
                    ...character,
                    companionWeapons: list
                })
            }
    }

    const companionWeapons = character.companionWeapons || [];

    return <Section
        title={"Companion Weapons"}
        actions={ [
            { action: () => setShowAddModal(true), label: <AddIcon /> }
        ]}>
        <NewModal show={showAddModal} onClose={() => setShowAddModal(false)} addNewCompanion={handleAddCompanion} />

        {comWeapon && <CompanionWeaponEditor weapon={comWeapon} setWeapon={handleModalSubmit} onClose={handleModalCancel} />}

        <HeadingBar>
            <Grid container paddingX={1}>
                <Grid item xs={10}><HeadingBarItem>Name</HeadingBarItem></Grid>
                <Grid item xs={2}><HeadingBarItem></HeadingBarItem></Grid>
            </Grid>
        </HeadingBar>
        <SimpleTable entries={companionWeapons.map(r => {
            return <>
                <Grid container>
                    <GridRow gridSize={10}><Typography style={{ textDecoration: "underline"}}>{r.name}</Typography></GridRow>
                    <GridRow gridSize={2} justifyContent="flex-end">
                        <EditIcon style={{ cursor: "pointer"}} onClick={() => setComWeapon(r)} />
                        <CancelIcon style={{ cursor: "pointer"}} onClick={() => handleRemove(r.id)} />
                    </GridRow>
                </Grid></>
        })} />
    </Section>
}

export default CompanionWeaponEditorList;